import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EntityType } from '@shared/models/entity-enum';
import { AppStorageService } from '@shared/services/app-storage.service';
import { L10nTranslationService } from 'angular-l10n';

export const NICE_CONFIRM_DIALOG_KEY = 'Confirm_Dialog_Who_Remember_Your_Choice';

export interface ConfirmData {
  title?: string;
  icon?: string;
  iconClass?: string;
  message?: string;
  multipleMessages?: string[];
  /**
   * When multiple messages are passed and isBulletedMessages, then the multiple messages are
   * rendered as ul/li template
   */
  isBulletedMessages?: boolean;
  confirmText?: string;
  cancelText?: string;
  leaveMessage?: string;
  swap?: boolean;
  rememberMyChoiceDialogId?: string;
  doNotRemindMeText?: string;
  translate?: boolean;
  duplicateAlert?: {
    existingId?: number;
    entity: EntityType;
  };
  showCloseButton?: boolean;
  rememberOnlyIfTrue?: boolean;
}

const defaultLeaveMessage = '';

@Component({
  selector: 'miq-confirm',
  templateUrl: './confirm-dialog.component.html',
  styleUrls: ['./confirm-dialog.component.scss'],
  standalone: false,
})
export class ConfirmDialogComponent implements OnInit, OnDestroy {
  public showDialog = false;
  public rememberChoice = false;
  public showCloseButton = true;
  public translateTitle = false;

  constructor(
    private dialogRef: MatDialogRef<ConfirmDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ConfirmData,
    public translationService: L10nTranslationService,
    private storage: AppStorageService
  ) {
    this.translateTitle = !!data.translate && !!data.title;
    if (data.translate) {
      data.message = data.message ? this.translationService.translate(data.message) : undefined;
      data.confirmText = data.confirmText ? this.translationService.translate(data.confirmText) : data.confirmText;
      data.cancelText = data.cancelText ? this.translationService.translate(data.cancelText) : undefined;
      data.leaveMessage = data.leaveMessage ? this.translationService.translate(data.leaveMessage) : undefined;
      data.doNotRemindMeText = data.doNotRemindMeText
        ? this.translationService.translate(data.doNotRemindMeText)
        : undefined;
    }

    if (data.rememberMyChoiceDialogId) {
      const choice = this.storage.remindTheirChoice(data.rememberMyChoiceDialogId, NICE_CONFIRM_DIALOG_KEY);
      if (choice !== null) {
        this.selectOption(choice);
        return;
      }
    }
    this.setData(data);
  }

  ngOnInit(): void {
    // required by angular-l10n
  }

  ngOnDestroy(): void {
    // required by angular-l10n
  }

  private saveTheirChoice(dialogId: string, choice: boolean) {
    const confirmDialogValues = this.storage.getItem(NICE_CONFIRM_DIALOG_KEY) || {};
    confirmDialogValues[dialogId] = choice;
    this.storage.setItem(NICE_CONFIRM_DIALOG_KEY, confirmDialogValues);
  }

  private setData({
    title = this.translationService.translate('shared.alert'),
    icon = '',
    iconClass = '',
    message = this.translationService.translate('shared.areYouSure'),
    confirmText = this.translationService.translate('shared.sYes'),
    doNotRemindMeText = this.translationService.translate('shared.doNotRemindMeDefaultText'),
    cancelText = null,
    swap = false,
    leaveMessage = defaultLeaveMessage,
    rememberMyChoiceDialogId = null,
    multipleMessages = [],
    duplicateAlert = null,
    showCloseButton = true,
    isBulletedMessages = false,
    rememberOnlyIfTrue = false,
  }) {
    this.data = {
      title,
      icon,
      iconClass,
      message,
      confirmText,
      cancelText,
      swap,
      multipleMessages,
      leaveMessage,
      rememberMyChoiceDialogId,
      doNotRemindMeText,
      duplicateAlert,
      isBulletedMessages,
      rememberOnlyIfTrue,
    };
    this.showDialog = true;
    this.showCloseButton = showCloseButton;
  }

  public selectOption(yesNo: boolean) {
    if (
      this.data.rememberMyChoiceDialogId != null &&
      this.rememberChoice &&
      (!this.data.rememberOnlyIfTrue || (this.data.rememberOnlyIfTrue && yesNo))
    ) {
      this.saveTheirChoice(this.data.rememberMyChoiceDialogId, yesNo);
    }
    this.dialogRef.close(yesNo);
  }
}
